import {
  Button,
  confirm,
  FormInput,
  FormRoot,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { TrashIcon } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import {
  CatalogSkuAlternateTypeSchema,
  type CatalogSkuSupersessionCreateInput,
} from "@/modules/catalog/domain/contracts";
import { toDateInputValue } from "@/shared/lib/date-input";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { CatalogSkuLookupItem } from "../api/client";
import {
  useCreateSkuAlternate,
  useCreateSkuSpec,
  useCreateSkuSupersession,
  useDeleteSkuAlternate,
  useDeleteSkuSpec,
  useDeleteSkuSupersession,
  useSkuAlternatesQuery,
  useSkuSpecsQuery,
  useSkuSupersessionsQuery,
} from "../api/queries";
import {
  catalogSkuLookupGetLabel,
  catalogSkuLookupGetValue,
  catalogSkuLookupPreloadQueryFn,
  catalogSkuLookupQueryFn,
  catalogSkuLookupQueryKey,
} from "../api/skuLookup";

const supersessionSchema = z.object({
  fromPartNo: z.string().min(1),
  toPartNo: z.string().min(1),
  toCatalogSkuId: z.string().optional(),
  supersededAt: z.string().optional(),
  notes: z.string().optional(),
});

const alternateSchema = z.object({
  altPartNo: z.string().min(1),
  altBrand: z.string().optional(),
  altType: z.string().optional(),
  notes: z.string().optional(),
});

const specSchema = z.object({
  specKey: z.string().min(1),
  specValue: z.string().min(1),
  specUnit: z.string().optional(),
});

const altTypeOptions = [
  { value: "__none__", label: "(ບໍ່ກຳນົດ)" },
  ...CatalogSkuAlternateTypeSchema.options.map((v) => ({
    value: v,
    label: v,
  })),
];

type SkuEPCModalProps = {
  skuId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function SkuEPCModal({ skuId, open, onOpenChange }: SkuEPCModalProps) {
  const ssQuery = useSkuSupersessionsQuery(skuId);
  const altQuery = useSkuAlternatesQuery(skuId);
  const specsQuery = useSkuSpecsQuery(skuId);

  const createSs = useCreateSkuSupersession(skuId);
  const deleteSs = useDeleteSkuSupersession(skuId);

  const createAlt = useCreateSkuAlternate(skuId);
  const deleteAlt = useDeleteSkuAlternate(skuId);

  const createSpec = useCreateSkuSpec(skuId);
  const deleteSpec = useDeleteSkuSpec(skuId);

  const ssDefaults = useMemo(
    (): z.infer<typeof supersessionSchema> => ({
      fromPartNo: "",
      toPartNo: "",
      toCatalogSkuId: "",
      supersededAt: "",
      notes: "",
    }),
    [],
  );

  const ssMethods = RHF.useForm({
    resolver: zodResolver(supersessionSchema),
    defaultValues: ssDefaults,
  });

  const altMethods = RHF.useForm({
    resolver: zodResolver(alternateSchema),
    defaultValues: {
      altPartNo: "",
      altBrand: "",
      altType: "__none__",
      notes: "",
    },
  });

  const specMethods = RHF.useForm({
    resolver: zodResolver(specSchema),
    defaultValues: {
      specKey: "",
      specValue: "",
      specUnit: "",
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ຂໍ້ມູນ EPC ຕໍ່ SKU"
      description="ສາຍດຕແທນ ທາງເລືອກ ແລະ ສັນຍາລັກສະເພາະ SKU"
      size="lg"
    >
      <div className="max-h-[70vh] space-y-8 overflow-y-auto pr-1">
        <section className="space-y-3">
          <h3 className="font-semibold text-foreground text-sm tracking-tight">
            Supersession (ອະດີດ → ປັດຈຸບັນ / ໜ້າເກົ່າ → ໃໝ່)
          </h3>
          {ssQuery.isLoading ? (
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-3 py-2 font-medium">ອະດີດ</th>
                    <th className="px-3 py-2 font-medium">ໄປ</th>
                    <th className="px-3 py-2 font-medium">SKU ປາຍທາງ</th>
                    <th className="px-3 py-2 font-medium">ວັນທີ່</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {(ssQuery.data ?? []).map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-3 py-1.5 font-mono text-xs">
                        {r.fromPartNo}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs">
                        {r.toPartNo}
                      </td>
                      <td className="px-3 py-1.5 text-xs">
                        {r.toCatalogSkuId ?? "—"}
                      </td>
                      <td className="px-3 py-1.5 text-xs">
                        {toDateInputValue(r.supersededAt) || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          aria-label="ລຶບ supersession"
                          onClick={async () => {
                            const ok = await confirm({
                              title: "ລຶບ supersession",
                              description: "ຕ້ອງການລຶບເສັ້ນນີ້ບໍ?",
                              actionText: "ລຶບ",
                              ActionProps: { variant: "destructive" },
                            });
                            if (ok) await deleteSs.mutateAsync(r.id);
                          }}
                        >
                          <TrashIcon className="size-4" aria-hidden />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {ssQuery.data?.length === 0 && (
                    <tr>
                      <td
                        className="px-3 py-2 text-muted-foreground text-sm"
                        colSpan={5}
                      >
                        ຍັງບໍ່ມີຂໍ້ມູນ supersession
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <FormRoot
            methods={ssMethods}
            className="grid gap-2 md:grid-cols-5"
            onSubmit={(vals) => {
              const body: CatalogSkuSupersessionCreateInput = {
                fromPartNo: vals.fromPartNo.trim(),
                toPartNo: vals.toPartNo.trim(),
                supersededAt: vals.supersededAt?.trim() ?? undefined,
                notes: vals.notes?.trim() ?? undefined,
                toCatalogSkuId: vals.toCatalogSkuId?.trim()?.length
                  ? vals.toCatalogSkuId.trim()
                  : null,
              };
              createSs.mutate(body, {
                onSuccess: () => {
                  ssMethods.reset(ssDefaults);
                },
              });
            }}
          >
            <FormInput
              name="fromPartNo"
              label="Part ອະດີດ"
              placeholder="11111-AAA"
            />
            <FormInput
              name="toPartNo"
              label="Part ໃໝ່"
              placeholder="11111-BBB"
            />
            <FormInfiniteCombobox<CatalogSkuLookupItem>
              name="toCatalogSkuId"
              label="SKU ປາຍທາງ (ທາງເລືອກ)"
              queryKey={[...catalogSkuLookupQueryKey]}
              queryFn={catalogSkuLookupQueryFn}
              preloadQueryFn={catalogSkuLookupPreloadQueryFn}
              getLabel={catalogSkuLookupGetLabel}
              getValue={catalogSkuLookupGetValue}
              placeholder="ຄົ້ນຫາຊື່ສິນຄ້າ · OEM · uuid..."
              clearable
            />
            <FormInput name="supersededAt" label="ວັນທີ່" type="date" />
            <FormInput name="notes" label="ໝາຍເຫດ" />
            <div className="flex justify-end md:col-span-5">
              <Button type="submit" size="sm" disabled={createSs.isPending}>
                ເພີ່ມ supersession
              </Button>
            </div>
          </FormRoot>
        </section>

        <section className="space-y-3 border-t pt-6">
          <h3 className="font-semibold text-sm">ເລກທາງເລືອກ / aftermarket</h3>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-3 py-2 font-medium">Part</th>
                  <th className="px-3 py-2 font-medium">ແບຣນດ໌</th>
                  <th className="px-3 py-2 font-medium">ປະເພດ</th>
                  <th className="px-3 py-2 font-medium">ໝາຍເຫດ</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {(altQuery.data ?? []).map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-1.5 font-mono text-xs">
                      {r.altPartNo}
                    </td>
                    <td className="px-3 py-1.5">{r.altBrand ?? "—"}</td>
                    <td className="px-3 py-1.5">{r.altType ?? "—"}</td>
                    <td className="px-3 py-1.5 text-xs">{r.notes ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ລຶບທາງເລືອກ",
                            description: "ຕ້ອງການລຶບບໍ?",
                            actionText: "ລຶບ",
                            ActionProps: { variant: "destructive" },
                          });
                          if (ok) await deleteAlt.mutateAsync(r.id);
                        }}
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <FormRoot
            methods={altMethods}
            className="grid gap-2 md:grid-cols-2"
            onSubmit={(vals) => {
              createAlt.mutate(
                {
                  altPartNo: vals.altPartNo.trim(),
                  altBrand: vals.altBrand?.trim()
                    ? vals.altBrand.trim()
                    : undefined,
                  altType:
                    vals.altType && vals.altType !== "__none__"
                      ? (vals.altType as
                          | "aftermarket"
                          | "oem_equivalent"
                          | "genuine_other")
                      : undefined,
                  notes: vals.notes?.trim() ?? undefined,
                },
                { onSuccess: () => altMethods.reset() },
              );
            }}
          >
            <FormInput name="altPartNo" label="ເລກພາກສ່ວນ" />
            <FormInput name="altBrand" label="ແບຣນດ໌" />
            <div className="space-y-1.5">
              <span className="font-medium text-foreground text-sm">ປະເພດ</span>
              <RHF.Controller
                name="altType"
                control={altMethods.control}
                render={({ field }) => (
                  <SimpleSelect
                    value={field.value ?? "__none__"}
                    onValueChange={field.onChange}
                    options={[...altTypeOptions]}
                  />
                )}
              />
            </div>
            <FormInput name="notes" label="ໝາຍເຫດ" />
            <div className="flex justify-end md:col-span-2">
              <Button type="submit" size="sm" disabled={createAlt.isPending}>
                ເພີ່ມທາງເລືອກ
              </Button>
            </div>
          </FormRoot>
        </section>

        <section className="space-y-3 border-t pt-6">
          <h3 className="font-semibold text-sm">Specs</h3>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-3 py-2 font-medium">Key</th>
                  <th className="px-3 py-2 font-medium">ຄ່າ</th>
                  <th className="px-3 py-2 font-medium">ໜ່ວຍ</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {(specsQuery.data ?? []).map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-1.5">{r.specKey}</td>
                    <td className="px-3 py-1.5">{r.specValue}</td>
                    <td className="px-3 py-1.5">{r.specUnit ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ລຶບ spec",
                            description: "ຕ້ອງການລຶບບໍ?",
                            actionText: "ລຶບ",
                            ActionProps: { variant: "destructive" },
                          });
                          if (ok) await deleteSpec.mutateAsync(r.id);
                        }}
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <FormRoot
            methods={specMethods}
            className="grid gap-2 md:grid-cols-3"
            onSubmit={(vals) => {
              createSpec.mutate(
                {
                  specKey: vals.specKey.trim(),
                  specValue: vals.specValue.trim(),
                  specUnit: vals.specUnit?.trim()
                    ? vals.specUnit.trim()
                    : undefined,
                },
                { onSuccess: () => specMethods.reset() },
              );
            }}
          >
            <FormInput name="specKey" label="Key" />
            <FormInput name="specValue" label="ຄ່າ" />
            <FormInput name="specUnit" label="ໜ່ວຍ" />
            <div className="flex justify-end md:col-span-3">
              <Button type="submit" size="sm" disabled={createSpec.isPending}>
                ເພີ່ມ spec
              </Button>
            </div>
          </FormRoot>
        </section>
      </div>
    </Modal>
  );
}
